import type { Agent } from '../../domain/entities/Agent.js';
import type { Client } from '../../domain/entities/Client.js';
import type { Order } from '../../domain/entities/Order.js';
import type { Product } from '../../domain/entities/Product.js';
import type { GamificationRules } from '../../domain/entities/GamificationRules.js';

const NOW = new Date();
const YEAR = NOW.getUTCFullYear();
const pad = (n: number) => String(n).padStart(2, '0');

export const MOCK_AGENT: Agent = {
  id: 'agent-001',
  email: 'agente.demo@losaniberia.com',
  name: 'Agente Demo',
  role: 'sales',
};

export const MOCK_CLIENTS: Client[] = [
  { id: 'c-001', commercialName: 'Bar Can Pere',         location: { lat: 41.5404, lng: 2.4456 }, revenueYTD: 18420, lastOrderDate: `${YEAR}-${pad(NOW.getUTCMonth() + 1)}-12` },
  { id: 'c-002', commercialName: 'Restaurant La Marina', location: { lat: 41.5512, lng: 2.4490 }, revenueYTD: 32100, lastOrderDate: `${YEAR}-${pad(NOW.getUTCMonth() + 1)}-04` },
  { id: 'c-003', commercialName: 'Hotel Costa Brava',    location: { lat: 41.6731, lng: 2.7892 }, revenueYTD: 51230, lastOrderDate: `${YEAR}-${pad(NOW.getUTCMonth() + 1)}-18` },
  { id: 'c-004', commercialName: 'Cafetería El Sol',     location: { lat: 41.5021, lng: 2.3756 }, revenueYTD: 4210,  lastOrderDate: `${YEAR}-${pad(Math.max(1, NOW.getUTCMonth()))}-22` },
  { id: 'c-005', commercialName: 'Cervecería Mataró',    location: { lat: 41.5388, lng: 2.4448 }, revenueYTD: 0,     lastOrderDate: null },
  { id: 'c-006', commercialName: 'Pizzería Vesuvio',     location: { lat: 41.4789, lng: 2.3501 }, revenueYTD: 9870,  lastOrderDate: `${YEAR}-${pad(NOW.getUTCMonth() + 1)}-09` },
];

export const MOCK_ORDERS: Order[] = (() => {
  const out: Order[] = [];
  let id = 1;
  const catalog = [
    { productId: 'p-1', category: 'Tops',     line: 'MAN',    unitPriceEUR: 12.5 },
    { productId: 'p-2', category: 'Bottoms',  line: 'MAN',    unitPriceEUR: 28 },
    { productId: 'p-3', category: 'Tops',     line: 'WOMAN',  unitPriceEUR: 18 },
    { productId: 'p-4', category: 'Dresses & Overalls', line: 'WOMAN', unitPriceEUR: 45 },
    { productId: 'p-5', category: 'Outerwear', line: 'JUNIOR', unitPriceEUR: 60 },
  ];
  for (let i = 0; i < 18; i++) {
    const d = new Date(Date.UTC(YEAR, NOW.getUTCMonth() - i, 5));
    for (const c of MOCK_CLIENTS) {
      if (c.id === 'c-005') continue;
      const lines = catalog.slice(0, 2 + (i % 3)).map((p) => ({
        ...p,
        quantity: 5 + Math.floor(Math.random() * 30),
      }));
      const total = lines.reduce((s, l) => s + l.quantity * l.unitPriceEUR, 0);
      out.push({
        id: `o-${id++}`,
        agentId: MOCK_AGENT.id,
        clientId: c.id,
        date: d.toISOString(),
        lines,
        totalEUR: Math.round(total * 100) / 100,
      });
    }
  }
  return out;
})();

export const MOCK_PRODUCTS: Product[] = [
  { sku: 'p-1', category: 'Tops',     line: 'MAN' },
  { sku: 'p-2', category: 'Bottoms',  line: 'MAN' },
  { sku: 'p-3', category: 'Tops',     line: 'WOMAN' },
  { sku: 'p-4', category: 'Dresses & Overalls', line: 'WOMAN' },
  { sku: 'p-5', category: 'Outerwear', line: 'JUNIOR' },
];

export const MOCK_TARGETS = {
  month:   { revenueEUR: 45000,  units: 1200,  clientCount: 6 },
  quarter: { revenueEUR: 135000, units: 3600,  clientCount: 7 },
  ytd:     { revenueEUR: 540000, units: 14400, clientCount: 8 },
} as const;

/**
 * Reglas de gamificación BORRADOR — proposición inicial para revisar con LOSAN.
 *
 * Las tres primeras son las recogidas en el brief original; las cinco
 * siguientes son propuestas creativas adaptadas al contexto fashion/textil
 * (múltiples categorías, líneas MAN/WOMAN/JUNIOR/KIDS, temporadas, márgenes).
 *
 * Cualquiera de estas reglas se puede modificar/eliminar/sustituir cambiando
 * solo este fichero — los predicados están centralizados en `gamification.ts`.
 */
export const MOCK_RULES: GamificationRules = {
  badges: [
    { id: 'super_vendedor',   label: 'Súper Vendedor',     description: 'Superar el 110% del objetivo de facturación mensual.' },
    { id: 'cazador_clientes', label: 'Cazador de Clientes', description: 'Incrementar un 15% el número de clientes vs año anterior.' },
    { id: 'ruta_perfecta',    label: 'Ruta Perfecta',       description: 'Reactivar 3 clientes que estaban en estado Rojo.' },
    { id: 'multi_categoria',  label: 'Multi-Categoría',     description: 'Vender en al menos 5 categorías distintas en el mes (Tops, Bottoms, Outerwear...).' },
    { id: 'cross_line',       label: 'Cross-Line',          description: 'Vender en las 4 líneas en el mes: MAN, WOMAN, TEEN y KIDS.' },
    { id: 'ticket_alto',      label: 'Ticket Alto',         description: 'Ticket medio por pedido un 10%+ por encima de la media de tu país.' },
    { id: 'sello_premium',    label: 'Sello Premium',       description: 'Mantener un margen comercial igual o superior al 35% en el mes.' },
    { id: 'comeback_cliente', label: 'Comeback',            description: 'Reactivar un cliente que llevaba 6+ meses sin pedidos.' },
  ],
  bonus: {
    // LOSAN agents typically earn 10–12% commission on their booked revenue.
    // Base 10% kicks in from 0–100% of target; rate ramps linearly to 12%
    // when the agent reaches 110% of target.
    baseCommissionPct: 0.10,
    maxCommissionPct: 0.12,
    tieredAtPctOfTarget: 1.10,
    minBonusEUR: 0,
  },
  reactivationThresholdForRutaPerfecta: 3,
  minCategoriesForMultiCategoria: 5,
  minLinesForCrossLine: 4,
  ticketAltoOverAveragePct: 0.10,
  selloPremiumMarginPct: 0.35,
  comebackDormantMonths: 6,
};
